import { Component, computed, inject, input, type OnInit, output, signal } from "@angular/core";
import { FormBuilder, type FormGroup, ReactiveFormsModule } from "@angular/forms";
import { TranslatePipe } from "@ngx-translate/core";

import { AdaaHelper } from "../../../../../core/utils";
import { Constants } from "../../../../constants/constants";
import { EntityPeriodFilterModel, SelectedPeriodModelType, ValueText } from "../../../../models";
import { LanguageService, PeriodSliderService, PropertiesService } from "../../../../services";
import { FormDropdownComponent, FormDropdownTreeComponent } from "../../../form";

@Component({
  selector: "adaa-entity-comparison-filters",
  imports: [TranslatePipe, ReactiveFormsModule, FormDropdownComponent, FormDropdownTreeComponent],
  templateUrl: "./entity-comparison-filters.html",
  styleUrl: "./entity-comparison-filters.scss",
})
export class EntityComparisonFiltersComponent implements OnInit {
  readonly languageService = inject(LanguageService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _propertiesService = inject(PropertiesService);
  private readonly _periodSliderService = inject(PeriodSliderService);

  showEntityTypeAndSize = input<boolean>(true);
  selectedPeriod = input<SelectedPeriodModelType>();
  filterChanged = output<EntityPeriodFilterModel>();
  entityTypes = output<ValueText[]>();
  entitySizes = output<ValueText[]>();

  readonly isPMOEntity = AdaaHelper.isPMOEntity;

  form: FormGroup;
  entityTypesOptions = signal<ValueText[]>([]);
  entitySizesOptions = signal<ValueText[]>([]);
  allQuarters: ValueText[] = [];

  periodOptions = computed<ValueText[]>(() => {
    const options: ValueText[] = [];
    this.allQuarters = [];

    this._periodSliderService.periodSlots().forEach((year) => {
      const quarters = year.quarters.map((q) => ({
        value: q.id,
        text: `Q${q.quarter} ${q.year}`,
      }));

      this.allQuarters = [...this.allQuarters, ...quarters];

      options.push({
        value: year.year,
        text: `${year.year}`,
        children: quarters,
      });
    });
    return options;
  });

  public ngOnInit() {
    this._getEntitesTypes();
    this._prepareForm();
  }

  public onFilterChange() {
    const period = this.form.value.period;
    const entitySize = this.form.value.entitySize;
    const entityType = this.form.value.entityType;

    const isYear = this._periodSliderService.periodSlots().some((e) => e.year === period);
    const quarter = !isYear ? this.allQuarters.find((e) => e.value === period) : undefined;

    this.filterChanged.emit({
      entitySize: isYear ? entitySize : 0,
      entityType: isYear ? entityType : 0,
      periodType: isYear ? "year" : "quarter",
      period: isYear ? period : quarter?.value,
      chartTitle: isYear ? `${period}` : quarter?.text,
    });
  }

  private _prepareForm(): void {
    this.form = this._formBuilder.group({
      entitySize: [0],
      entityType: [0],
      period: [
        this.selectedPeriod()
          ? this.selectedPeriod()?.type === "year"
            ? this.selectedPeriod()?.period.year
            : this.selectedPeriod()?.period.id
          : undefined,
      ],
    });
  }

  private _getEntitesTypes(): void {
    const allEntitiesOption: ValueText = {
      value: 0,
      text: this.languageService.translate("national_projects.all_entities"),
    };

    this._propertiesService
      .getPropByIdList([Constants.CONSTANT_ENTITY_TYPE, Constants.CONSTANT_ENTITY_SIZE])
      .subscribe((response) => {
        if (response.inError) return;
        const entitySizes = response.responseData.filter((e) => e.propType == Constants.CONSTANT_ENTITY_SIZE);
        this.entitySizesOptions.set(
          AdaaHelper.setDropdownArray(entitySizes, "id", AdaaHelper.getFieldLanguage("name"))
        );

        const entityTypes = response.responseData.filter((e) => e.propType == Constants.CONSTANT_ENTITY_TYPE);
        this.entityTypesOptions.set(
          AdaaHelper.setDropdownArray(entityTypes, "id", AdaaHelper.getFieldLanguage("name"))
        );

        this.entitySizesOptions().unshift(allEntitiesOption);
        this.entityTypesOptions().unshift(allEntitiesOption);

        this.entitySizes.emit(this.entitySizesOptions());
        this.entityTypes.emit(this.entityTypesOptions());
      });
  }
}
